import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useActionReviewStagedTradingContract } from "@/lib/api/otc";

export function useOtcContractHandlers() {
  const { toast } = useToast();
  const [isUnstaging, setIsUnstaging] = useState(false);
  const [unstageModalId, setUnstageModalId] = useState<string | undefined>();
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteModalId, setDeleteModalId] = useState<string | undefined>();

  const actionReviewStagedContract = useActionReviewStagedTradingContract();

  // Handler to open unstage modal
  const confirmUnstagePrompt = ({ id }: { id: string }) => {
    setUnstageModalId(id);
  };

  // Handler to unstage contract
  const onUnstage = async ({ id }: { id: string }) => {
    if (isUnstaging) return;
    setIsUnstaging(true);

    try {
      const result = await actionReviewStagedContract({
        id,
        should_delete: false,
      });

      if (!result.success) {
        throw result;
      }

      toast({
        title: "Contract Approved",
        description: "The contract has been successfully approved.",
      });
      setUnstageModalId(undefined);
    } catch (error) {
      toast({
        title: "Failed to unstage contract",
        description: "An error occurred while unstaging the contract.",
      });
    } finally {
      setIsUnstaging(false);
    }
  };

  // Handler to open delete modal
  const confirmDeletePrompt = ({ id }: { id: string }) => {
    setDeleteModalId(id);
  };

  // Handler to delete contract
  const onDelete = async ({ id }: { id: string }) => {
    if (isDeleting) return;
    setIsDeleting(true);

    try {
      const result = await actionReviewStagedContract({
        id,
        should_delete: true,
      });

      if (!result.success) {
        throw result;
      }

      toast({
        title: "Contract Deleted",
        description: "The contract has been successfully deleted.",
      });
      setDeleteModalId(undefined);
    } catch (error) {
      toast({
        title: "Failed to delete contract",
        description: "An error occurred while deleting the contract.",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    isUnstaging,
    isDeleting,
    unstageModalId,
    deleteModalId,
    setUnstageModalId,
    setDeleteModalId,
    confirmUnstagePrompt,
    confirmDeletePrompt,
    onUnstage,
    onDelete,
  };
}

